// src/components/PDF/ProtectPDF.js
import React, { useState } from 'react';
import {
  Upload, Lock, ShieldCheck, Eye, EyeOff, FileText,
  Printer, Copy, Edit3, CheckSquare, X, Download
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { pdfService } from '../../services/pdfService';
import PDFPreviewSave from './PDFPreviewSave';
import { useAuthGuard } from '../../hooks/useAuthGuard';

const ProtectPDF = ({ isLoading, setIsLoading }) => {
  const { requireAuth, AuthModal } = useAuthGuard();
  const [pdfFile, setPdfFile] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [protectedBlob, setProtectedBlob] = useState(null);
  const [showPreviewSave, setShowPreviewSave] = useState(false);
  
  const [permissions, setPermissions] = useState({
    allowPrinting: true,
    allowCopying: false,
    allowModifying: false,
    allowAnnotating: true,
    allowFillingForms: true
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    onDrop: (files) => {
      setPdfFile(files[0]);
      setProtectedBlob(null); // Reset results if a new file is uploaded
    }
  });

  const togglePermission = (key) => {
    setPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Helper function to trigger browser download (raw, no auth check)
  const triggerDownload = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `protected_${fileName}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Auth-guarded download
  const downloadFile = (blob, fileName) => {
    requireAuth(() => triggerDownload(blob, fileName));
  };

  const handleProtect = async () => {
    if (!password) return toast.error('Please enter a password');

    setIsLoading(true);
    try {
      const blob = await pdfService.protectPDF(pdfFile, password, permissions);
      setProtectedBlob(blob);

      // Trigger download with auth guard
      downloadFile(blob, pdfFile.name);

      toast.success('PDF Protected!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 mb-6">
        <ShieldCheck className="w-8 h-8 text-[#232751]" />
        <h2 className="text-2xl font-semibold text-gray-800">Security & Permissions</h2>
      </div>

      {!pdfFile ? (
        <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${isDragActive ? 'border-[#232751] bg-[#232751]/5' : 'border-gray-300 hover:border-[#232751]/70'}`}>
          <input {...getInputProps()} />
          <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600 font-medium">Click to upload or drag & drop</p>
          <p className="text-xs text-gray-400 mt-2">Encryption happens entirely in your browser</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* File Info Card */}
          <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between border">
            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6 text-red-500" />
              <div>
                <p className="font-medium text-gray-700 truncate max-w-[200px]">{pdfFile.name}</p>
                <p className="text-xs text-gray-500">{(pdfFile.size / 1024).toFixed(1)} KB</p>
              </div>
            </div>
            <button onClick={() => { setPdfFile(null); setProtectedBlob(null); }} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
              <X size={18} className="text-gray-500" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Password Config */}
            <div className="bg-white border rounded-xl p-6 shadow-sm space-y-4">
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Access Control</h3>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Set Opening Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password..."
                    className="w-full pl-4 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-[#232751] outline-none transition-all"
                  />
                  <button 
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Permissions Config */}
            <div className="bg-white border rounded-xl p-6 shadow-sm space-y-4">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Restrictions</h3>
              <div className="space-y-3">
                {[
                  { id: 'allowPrinting', label: 'Allow Printing', icon: Printer },
                  { id: 'allowCopying', label: 'Allow Text Copying', icon: Copy },
                  { id: 'allowModifying', label: 'Allow Modifying', icon: Edit3 },
                  { id: 'allowFillingForms', label: 'Allow Form Filling', icon: CheckSquare },
                ].map((item) => (
                  <div key={item.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <item.icon size={16} />
                      <span className="text-sm">{item.label}</span>
                    </div>
                    <button
                      onClick={() => togglePermission(item.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${permissions[item.id] ? 'bg-[#232751]' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${permissions[item.id] ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={handleProtect}
            disabled={isLoading || !password}
            className="w-full bg-[#232751] text-white py-4 rounded-xl font-bold hover:bg-[#1b1f43] transition-all shadow-lg flex items-center justify-center disabled:opacity-50"
          >
            <Lock className="w-5 h-5 mr-2" />
            {isLoading ? 'Encrypting...' : 'Encrypt & Download'}
          </button>
        </div>
      )}

      {/* Results and Action Buttons */}
      {protectedBlob && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between animate-in slide-in-from-bottom-2">
          <div className="mb-4 sm:mb-0">
            <p className="text-green-900 font-bold">PDF Successfully Secured</p>
            <p className="text-sm text-green-700">The file has been encrypted with your chosen settings.</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => downloadFile(protectedBlob, pdfFile.name)}
              className="bg-green-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-700 transition-all flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Again
            </button>
            <button
              onClick={() => setShowPreviewSave(true)}
              className="bg-white border border-green-300 text-green-700 px-6 py-2 rounded-lg font-medium hover:bg-green-50 transition-all"
            >
              Save to Library
            </button>
          </div>
        </div>
      )}

      {AuthModal}

      <PDFPreviewSave
        isOpen={showPreviewSave}
        onClose={() => setShowPreviewSave(false)}
        pdfBlob={protectedBlob}
        fileName={`protected_${pdfFile?.name}`}
        operationType="protect"
      />
    </div>
  );
};

export default ProtectPDF;
